import React, { useEffect } from "react";
import PropTypes from "prop-types";
import Link from "next/link";
import classNames from "classnames";
import Tooltip from "@JohnLewisPartnership/jl-design-system/dist/components/tooltip/Tooltip";
import { SecondaryButton } from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import { ChevronLeftOutlined as ChevronLeft } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/ChevronLeftOutlined";
import { ChevronRightOutlined as ChevronRight } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/ChevronRightOutlined";
import { Message } from "@JohnLewisPartnership/jl-design-system/dist/components/message/Message";
import { useRouter } from "next/router";
import { INFO_TYPES, OPTION_TYPES } from "../../data/content/returns";
import SolutionESI from "./SolutionESI";
import SolutionViewContactDetails from "./SolutionViewContactDetails";
import styles from "./DecisionTree.module.scss";
import { useIsApp } from "../../hooks";
import AgentForceChatBotContainer from "../af-chatbot-container";
import AFChatOption from "../af-chatbot-container/AFChatOption";
import { useLiveChat } from "../../hooks";

const customerServicesLink = {
  href: "/",
  title: "Customer Services",
};

function OptionButton({
  optionType,
  url,
  className,
  buttonLabel,
  imageSrc,
  imageAlt,
  subTitle,
  chatConfig,
  isExperimentLoaded,
  isExperimentVariant,
}) {
  switch (optionType) {
    case OPTION_TYPES.SPACER:
      // Show spacer if:
      // - Experiment is loaded and variant is "Experiment", OR
      // - Experiment is NOT loaded and chatConfig exists
      if ((isExperimentLoaded && isExperimentVariant) || (!isExperimentLoaded && chatConfig)) {
        return <div className={styles.spacer} />;
      }
      return null;
    case OPTION_TYPES.AGENT_FORCE_CHAT_BOT:
      // Case 1: Experiment IS loaded (Monetate returned a value)
      if (isExperimentLoaded) {
        // Only check variant label, ignore chatConfig completely
        if (isExperimentVariant) {
          console.log("[DecisionTree] Rendering AgentForceChatBotContainer — Experiment variant active");
          return <AgentForceChatBotContainer />;
        }
        // Control variant or any other variant → show nothing
        console.log("[DecisionTree] Skipping — Experiment loaded but variant is not 'Experiment'");
        return null;
      }
      if (chatConfig) {
        console.log("[DecisionTree] Rendering AFChatOption — Experiment not loaded, using chatConfig fallback");
        return <AFChatOption className={styles.afchatOption} config={chatConfig.chat} />;
      }

      console.log("[DecisionTree] Skipping — No experiment and no chatConfig available");
      return null;
    default:
      return (
        <Link href={url} scroll={false} shallow>
          <SecondaryButton className={classNames(styles.optionButton, styles[className])}>
            {buttonLabel}
            {imageSrc && imageAlt && <img src={imageSrc} alt={imageAlt} />}
            {subTitle && <span>{subTitle}</span>}
            <div>
              <ChevronRight />
            </div>
          </SecondaryButton>
        </Link>
      );
  }
}

OptionButton.propTypes = PropTypes.shape({
  optionType: PropTypes.string,
  buttonLabel: PropTypes.string,
  url: PropTypes.string,
  nextPage: PropTypes.string,
  chatConfig: PropTypes.shape({}),
  isExperimentLoaded: PropTypes.bool,
  isExperimentVariant: PropTypes.bool,
}).isRequired;

function LandingPageDecisionTree({
  showBack = true,
  content,
  startKey,
  defaultBackText = "",
  onTreeUpdate,
  uberEatsRedirect,
  isExperimentLoaded = false,
  isExperimentVariant = false,
}) {
  const router = useRouter();
  const isApp = useIsApp();
  const elements = router.query.dt ? router.query.dt.split("-") : [];
  const size = elements.length;
  const startElement = content[startKey];
  const id = size > 0 && elements[size - 1] ? elements[size - 1] : startKey;
  const tree = size > 0 && content[elements[size - 1]] ? content[elements[size - 1]] : startElement;
  const treeNext = size > 1 && content[elements[size - 2]] ? content[elements[size - 2]] : startElement;
  const backText = tree.breadcrumbTitle || treeNext.title || treeNext.breadcrumbTitle || defaultBackText;
  const backLinkText = size === 0 ? customerServicesLink.title : backText;
  const backLink = size === 0 ? customerServicesLink.href : tree.breadcrumbHref || router.pathname;

  const { chatConfig } = useLiveChat(startElement?.getSolutionsForCurrentTag ? startKey : null);

  useEffect(() => {
    onTreeUpdate(elements);
    if (size > 0 && !content[elements[size - 1]]) {
      console.log("decision tree path not found", router.asPath);
    }
  }, [size, tree, elements]);

  const navigateTo = name => {
    router.push(
      {
        pathname: router.pathname,
        query: {
          ...router.query,
          dt: elements.concat([name]).join("-"),
        },
      },
      undefined,
      {
        shallow: true,
        scroll: false,
      },
    );
    window.scrollTo(0, 0);
  };

  const createInformationItem = (info, i) => {
    switch (info.informationType) {
      case INFO_TYPES.TOOLTIP_DETAILS:
        if (info.label && info.details) {
          return (
            <Tooltip
              id={`info_${i}_${info.label}`}
              key={`info_${i}_${info.label}`}
              buttonLabel={info.label}
              popoverClassname={styles.tooltip}
              icon
            >
              {info.details}
            </Tooltip>
          );
        }
        return null;
      default:
        return null;
    }
  };

  return (
    <>
      {showBack && !(isApp && size === 0) && (
        <div className={styles.breadcrumb}>
          <Link
            href={{
              pathname: backLink,
              query: {
                ...router.query,
                dt: elements.filter((_, index) => index !== size - 1).join("-"),
              },
            }}
            scroll={false}
            shallow
          >
            <ChevronLeft />
            {backLinkText}
          </Link>
        </div>
      )}
      <div className={styles.background}>
        <div className={classNames("util-container", styles.decisionTreeContainer)}>
          {tree.title && <h2 className={styles.decisionTreeContainerHeading}>{tree.title}</h2>}
          {tree.subTitle && <h2>{tree.subTitle}</h2>}
          {tree.options && (
            <div
              className={classNames(
                styles.optionsContainer,
                tree.optionsContainerClass && styles[tree.optionsContainerClass],
              )}
            >
              {tree.options?.map(option => {
                const url = option.url
                  ? option.url
                  : {
                      pathname: router.pathname,
                      query: {
                        ...router.query,
                        dt: elements.concat([option.nextPage]).join("-"),
                      },
                    };
                return (
                  <OptionButton
                    key={`${JSON.stringify(option)}`}
                    {...option}
                    url={url}
                    chatConfig={chatConfig}
                    isExperimentLoaded={isExperimentLoaded}
                    isExperimentVariant={isExperimentVariant}
                  />
                );
              })}
            </div>
          )}
          {tree.information && (
            <div className={styles.infoContainer}>{tree.information?.map(createInformationItem)}</div>
          )}
          {tree.title === "Returning an item" && uberEatsRedirect && (
            <div className="uberEatsWrapper">
              <div className={styles.uberEatsInformationContainer}>
                <Message
                  body=""
                  buttonProps={{
                    href: "https://www.johnlewis.com/our-services/uber",
                    rel: "noopener noreferrer",
                    tag: "a",
                  }}
                  buttonText="Find out how on our information page"
                  title="Returning an order placed on Uber Eats?"
                  type="information"
                  whiteBackground
                />
              </div>
            </div>
          )}
          {tree.solution && (
            <tree.solution id={id} onGoToContentSection={navigateTo} esiSource={tree.esiContent} {...tree} />
          )}
          <div>
            {tree.esiContent && !tree.solution && <SolutionESI esiSource={tree.esiContent} />}
            {tree.viewContactDetails && <SolutionViewContactDetails contactDetailsTag={tree.viewContactDetails} />}
          </div>
        </div>
      </div>
    </>
  );
}

LandingPageDecisionTree.propTypes = {
  showBack: PropTypes.bool,
  defaultBackText: PropTypes.string,
  onTreeUpdate: PropTypes.func.isRequired,
  content: PropTypes.shape({}).isRequired,
  startKey: PropTypes.string.isRequired,
  uberEatsRedirect: PropTypes.bool,
  isExperimentLoaded: PropTypes.bool,
  isExperimentVariant: PropTypes.bool,
};

export default LandingPageDecisionTree;
